import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";
import { PARTNERS_ENABLED } from "@/lib/featureFlags";

// Lazy load non-critical routes to reduce initial bundle size
const AboutUs = lazy(() => import("./pages/AboutUs"));
const OurPartners = lazy(() => import("./pages/OurPartners"));
const InvestInRioplex = lazy(() => import("./pages/InvestInRioplex"));
const MainSectors = lazy(() => import("./pages/MainSectors"));
const DiscoverRioplex = lazy(() => import("./pages/DiscoverRioplex"));
const NewsResources = lazy(() => import("./pages/NewsResources"));
const BecomeAMember = lazy(() => import("./pages/BecomeAMember"));
const Contact = lazy(() => import("./pages/Contact"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
// Register page hidden — registration is admin-controlled only (admin invites via /admin/users)
const CompleteProfile = lazy(() => import("./pages/CompleteProfile"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Portal = lazy(() => import("./pages/Portal"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminNewsEvents = lazy(() => import("./pages/AdminNewsEvents"));
const AdminPosts = lazy(() => import("./pages/AdminPosts"));
const AdminAuthors = lazy(() => import("./pages/AdminAuthors"));
const AdminCategories = lazy(() => import("./pages/AdminCategories"));
const AdminSEO = lazy(() => import("./pages/AdminSEO"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminTickets = lazy(() => import("./pages/AdminTickets"));
const AdminTicketDetail = lazy(() => import("./pages/AdminTicketDetail"));
const PostDetail = lazy(() => import("./pages/PostDetail"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPostDetail = lazy(() => import("./pages/BlogPostDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminLayout = lazy(() => import("./components/AdminLayout").then(m => ({ default: m.AdminLayout })));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/about" element={<Suspense fallback={null}><AboutUs /></Suspense>} />
          {/* Our Partners is gated: visible in local dev, hidden on the live site
              until it's ready. Flip PARTNERS_ENABLED to launch it. */}
          {PARTNERS_ENABLED && (
            <Route path="/partners" element={<Suspense fallback={null}><OurPartners /></Suspense>} />
          )}
          <Route path="/invest" element={<Navigate to="/invest/sectors" replace />} />
          <Route path="/invest/sectors" element={<Suspense fallback={null}><MainSectors /></Suspense>} />
          <Route path="/discover" element={<Suspense fallback={null}><DiscoverRioplex /></Suspense>} />
          <Route path="/news" element={<Suspense fallback={null}><NewsResources /></Suspense>} />
          <Route path="/membership" element={<Suspense fallback={null}><BecomeAMember /></Suspense>} />
          <Route path="/contact" element={<Suspense fallback={null}><Contact /></Suspense>} />
          <Route path="/news/:slug" element={<Suspense fallback={null}><PostDetail /></Suspense>} />
          <Route path="/blog" element={<Suspense fallback={null}><Blog /></Suspense>} />
          <Route path="/blog/:slug" element={<Suspense fallback={null}><BlogPostDetail /></Suspense>} />
          <Route path="/admin-login" element={<Suspense fallback={null}><AdminLogin /></Suspense>} />
          <Route path="/auth/complete-profile" element={<Suspense fallback={null}><CompleteProfile /></Suspense>} />
          <Route path="/forgot-password" element={<Suspense fallback={null}><ForgotPassword /></Suspense>} />
          <Route path="/reset-password" element={<Suspense fallback={null}><ResetPassword /></Suspense>} />
          <Route path="/portal" element={<Suspense fallback={null}><Portal /></Suspense>} />
          {/* Register route removed — registration is admin-controlled via /admin/users */}
          <Route path="/admin" element={<Suspense fallback={null}><AdminLayout /></Suspense>}>
            <Route index element={<Suspense fallback={null}><AdminDashboard /></Suspense>} />
            <Route path="users" element={<Suspense fallback={null}><AdminUsers /></Suspense>} />
            <Route path="tickets" element={<Suspense fallback={null}><AdminTickets /></Suspense>} />
            <Route path="tickets/:id" element={<Suspense fallback={null}><AdminTicketDetail /></Suspense>} />
            <Route path="news" element={<Suspense fallback={null}><AdminNewsEvents /></Suspense>} />
            <Route path="posts" element={<Suspense fallback={null}><AdminPosts /></Suspense>} />
            <Route path="authors" element={<Suspense fallback={null}><AdminAuthors /></Suspense>} />
            <Route path="categories" element={<Suspense fallback={null}><AdminCategories /></Suspense>} />
            <Route path="seo" element={<Suspense fallback={null}><AdminSEO /></Suspense>} />
            <Route path="settings" element={<Suspense fallback={null}><AdminSettings /></Suspense>} />
          </Route>
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<Suspense fallback={null}><NotFound /></Suspense>} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
